import { create } from 'zustand'
import type { HubSpotDeal, DealHubSession, SessionUser } from './types'
import { getAuthHeaders } from '@api/client'
import { useAuthStore } from '@store/authStore'

// Parse the contributors array returned by the /api/deals list endpoint.
// Gracefully handles deals that pre-date the contributors query (array missing).
function parseContributors(raw: unknown, fallback: SessionUser | undefined): SessionUser[] {
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .filter((c): c is { email: string; name?: string | null } => !!c && typeof (c as any).email === 'string')
      .map(c => ({ id: c.email, email: c.email, name: c.name || c.email }))
  }
  return fallback ? [fallback] : []
}

function toSessionUser(email?: string | null, name?: string | null): SessionUser | undefined {
  if (!email) return undefined
  return { id: email, email, name: name || email }
}

const API_BASE = '/api'

export interface PaginationState {
  currentPage: number
  totalPages: number
  totalSessions: number
  pageSize: number
}

interface HomeStore {
  // Search state
  searchQuery: string
  searchResults: HubSpotDeal[]
  isSearching: boolean
  hasSearched: boolean
  searchError: string | null

  // Selected deal
  selectedDeal: HubSpotDeal | null

  // Sessions for selected deal
  sessions: DealHubSession[]
  isLoadingSessions: boolean

  // All recent sessions (for initial view)
  recentSessions: DealHubSession[]
  isLoadingRecent: boolean

  // Pagination
  pagination: PaginationState

  // Actions
  setSearchQuery: (query: string) => void
  searchDeals: (query: string) => Promise<void>
  clearSearch: () => void
  selectDeal: (deal: HubSpotDeal | null) => void
  loadSessionsForDeal: (dealId: string) => Promise<void>
  loadRecentSessions: (page?: number) => Promise<void>
  createSession: (deal: HubSpotDeal) => Promise<DealHubSession>
  deleteSession: (sessionId: string) => Promise<boolean>
}

export const useHomeStore = create<HomeStore>((set, get) => ({
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  hasSearched: false,
  searchError: null,
  selectedDeal: null,
  sessions: [],
  isLoadingSessions: false,
  recentSessions: [],
  isLoadingRecent: false,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalSessions: 0,
    pageSize: 10,
  },

  setSearchQuery: (query) => set({ searchQuery: query }),

  searchDeals: async (query) => {
    set({ isSearching: true, hasSearched: true, searchError: null })

    try {
      const response = await fetch(`${API_BASE}/hubspot/search?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to search HubSpot')
      }

      const data = await response.json()

      // Transform HubSpot API response to our format
      // Note: id is temporarily the HubSpot deal ID until a DealHub session is created
      const results: HubSpotDeal[] = (data.results || []).map((deal: any) => ({
        id: deal.id,
        hubspotDealId: deal.id,
        name: deal.properties?.dealname || 'Unnamed Deal',
        amount: deal.properties?.amount ? parseFloat(deal.properties.amount) : null,
        stage: deal.properties?.dealstage || 'Unknown',
        closeDate: deal.properties?.closedate || null,
        ownerName: deal.properties?.hubspot_owner_id || 'Unassigned',
        companyName: deal.properties?.company || null,
        createdAt: deal.createdAt || new Date().toISOString(),
      }))

      set({ searchResults: results, isSearching: false })
    } catch (error) {
      console.error('HubSpot search error:', error)
      set({
        searchResults: [],
        isSearching: false,
        searchError: error instanceof Error ? error.message : 'Search failed'
      })
    }
  },

  clearSearch: () =>
    set({
      searchQuery: '',
      searchResults: [],
      hasSearched: false,
      selectedDeal: null,
      sessions: [],
      searchError: null,
    }),

  selectDeal: (deal) => {
    set({ selectedDeal: deal })
    if (deal) {
      // Use hubspotDealId for loading sessions (searches by HubSpot deal ID in the DB)
      get().loadSessionsForDeal(deal.hubspotDealId || deal.id)
    } else {
      set({ sessions: [] })
    }
  },

  loadSessionsForDeal: async (dealId) => {
    set({ isLoadingSessions: true })

    try {
      // Fetch deals that are linked to this HubSpot deal (search by hubspot_deal_id)
      const response = await fetch(`${API_BASE}/deals?hubspot_deal_id=${encodeURIComponent(dealId)}`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to load sessions')
      }

      const data = await response.json()

      // Transform deals to sessions format
      const dealSessions: DealHubSession[] = (data.items || []).map((deal: any) => {
        const createdBy = toSessionUser(deal.created_by, deal.created_by_name)
        // Last Modified falls back to created_by for legacy deals that pre-date
        // migration 057 (which added updated_by columns).
        const lastModifiedBy = toSessionUser(
          deal.updated_by || deal.created_by,
          deal.updated_by_name || deal.created_by_name,
        )
        return {
          id: deal.id.toString(),
          hubspotDealId: deal.hubspot_deal_id,
          hubspotDealName: deal.hubspot_deal_name || deal.customer_name,
          companyName: deal.customer_name,
          hasQuote: !!deal.quote,
          hasScope: !!deal.scope,
          hasROI: !!deal.roi,
          contributors: parseContributors(deal.contributors, createdBy),
          createdAt: deal.created_at,
          createdBy,
          lastModifiedAt: deal.updated_at,
          lastModifiedBy,
        }
      })

      set({ sessions: dealSessions, isLoadingSessions: false })
    } catch (error) {
      console.error('Load sessions error:', error)
      set({ sessions: [], isLoadingSessions: false })
    }
  },

  loadRecentSessions: async (page = 1) => {
    set({ isLoadingRecent: true })

    try {
      const { pagination } = get()
      const pageSize = pagination.pageSize

      // Fetch recent deals from our database with pagination
      const response = await fetch(`${API_BASE}/deals?page=${page}&pageSize=${pageSize}`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to load recent sessions')
      }

      const data = await response.json()

      // Transform deals to sessions format
      const recentSessions: DealHubSession[] = (data.items || []).map((deal: any) => {
        const createdBy = toSessionUser(deal.created_by, deal.created_by_name)
        const lastModifiedBy = toSessionUser(
          deal.updated_by || deal.created_by,
          deal.updated_by_name || deal.created_by_name,
        )
        return {
          id: deal.id.toString(),
          hubspotDealId: deal.hubspot_deal_id,
          hubspotDealName: deal.hubspot_deal_name || deal.customer_name,
          companyName: deal.customer_name,
          hasQuote: !!deal.quote,
          hasScope: !!deal.scope,
          hasROI: !!deal.roi,
          contributors: parseContributors(deal.contributors, createdBy),
          createdAt: deal.created_at,
          createdBy,
          lastModifiedAt: deal.updated_at,
          lastModifiedBy,
        }
      })

      set({
        recentSessions,
        isLoadingRecent: false,
        pagination: {
          currentPage: data.page || page,
          totalPages: data.totalPages || 1,
          totalSessions: data.total || 0,
          pageSize,
        },
      })
    } catch (error) {
      console.error('Load recent sessions error:', error)
      set({ recentSessions: [], isLoadingRecent: false })
    }
  },

  createSession: async (deal) => {
    try {
      // Pull the authenticated user from the store so the creator is attributed
      // correctly on the client. The server will override with the verified
      // identity from the JWT, but including it here keeps dev mode honest.
      const currentUser = useAuthStore.getState().user

      const response = await fetch(`${API_BASE}/deals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customer_name: deal.companyName || deal.name,
          hubspot_deal_id: deal.hubspotDealId || deal.id,
          hubspot_deal_name: deal.name,
          created_by: currentUser?.email || null,
          created_by_name: currentUser?.name || currentUser?.email || null,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create session')
      }

      const newDeal = await response.json()
      const createdBy = toSessionUser(newDeal.created_by, newDeal.created_by_name)

      const newSession: DealHubSession = {
        id: newDeal.id.toString(),
        hubspotDealId: newDeal.hubspot_deal_id,
        hubspotDealName: newDeal.hubspot_deal_name,
        companyName: newDeal.customer_name,
        hasQuote: false,
        hasScope: false,
        hasROI: false,
        contributors: createdBy ? [createdBy] : [],
        createdAt: newDeal.created_at,
        createdBy: createdBy as any,
        lastModifiedAt: newDeal.updated_at,
        lastModifiedBy: createdBy as any,
      }

      set((state) => ({
        sessions: [newSession, ...state.sessions],
        recentSessions: [newSession, ...state.recentSessions],
      }))

      return newSession
    } catch (error) {
      console.error('Create session error:', error)
      throw error
    }
  },

  deleteSession: async (sessionId: string) => {
    try {
      const response = await fetch(`${API_BASE}/deals/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to delete session')
      }

      // Remove from both lists and update pagination total
      set((state) => {
        const newTotal = Math.max(0, state.pagination.totalSessions - 1)
        return {
          sessions: state.sessions.filter(s => s.id !== sessionId),
          recentSessions: state.recentSessions.filter(s => s.id !== sessionId),
          pagination: {
            ...state.pagination,
            totalSessions: newTotal,
            totalPages: Math.max(1, Math.ceil(newTotal / state.pagination.pageSize)),
          },
        }
      })

      return true
    } catch (error) {
      console.error('Delete session error:', error)
      return false
    }
  },
}))
